import {
  CardStyleInterpolators,
  StackScreenProps,
} from "@react-navigation/stack";
import {
  ModalCloseButton,
  RootDrawerToggle,
  useThemeProvider,
  Text,
  createStackHeaderNavigator,
  ActiveUserAvatar,
  TextButton,
  Tooltip,
} from "@towersystems/roam-common-native";
import { FilterMetadataController } from "../../../../components";
import {
  AuthorizedAppRootScreenParamList,
  RegisterRootParamList,
  ValidSetupRootParamList,
} from "../../../../types";
import { PermissionsModalRootScreen } from "../../../permission/screens";
import {
  CreateTileProvider,
  EditTileProvider,
  LayoutProvider,
  RegisterProvider,
} from "../../components";
import {
  useAddItem,
  useSaleControllerProvider,
} from "../../utils/sale-controller";
import { CartItemModalRootScreen } from "../CartItemModalRootScreen";
import { CheckoutRootScreen } from "../CheckoutRootScreen/CheckoutRootScreen";
import { CreateQuickKeyRootScreen } from "../CreateQuickKeyRootScreen";
import { EditQuickKeyRootScreen } from "../EditQuickKeyRootScreen";
import { MobileCartScreen } from "../MobileCartScreen";
import {
  ProductFilteredListScreen,
  useProductFilteredSearchListener,
} from "../ProductFilteredListScreen";
import { ProductScannerScreen } from "../ProductScannerScreen";
import { SellScreen } from "../SellScreen";
import { SerialNumberRootScreen } from "../SerialNumberRootScreen";
import { VariantPickerModalScreen } from "../VariantPickerModalScreen";
import { ActionsRootScreen } from "../ActionsRootScreen";
import { Logo } from "../../../../components/Logo/Logo";
import { Stack as ViewStack } from "@towersystems/roam-common-native";
import { VisuallyHidden } from "../../../../components/VisuallyHidden";

import { AddNonPricedItemModalScreen } from "../AddNonPricedItemModalScreen";
import { AddGiftCardProductCartRootScreen } from "../AddGiftCardProductCartScreen";
import { ConfirmAddScannedCustomerModalScreen } from "../ConfirmAddScannedCustomerModalScreen";
import { ValidateActiveSale } from "../../components/ValidateActiveSale";
import { KitResolverModalScreen } from "../KitResolverModalScreen";
import { View } from "react-native-ui-lib";
import { useActiveTerminal } from "../../../../utilities/app-state/hooks";
import {
  formatDateToCalendar,
  fullDateTimeFormat,
} from "@towersystems/roam-common/lib/shared-utils";
import { Ionicons } from "@expo/vector-icons";
import { AiInsightsRootModalScreen } from "../AiInsightsScreens/AiInsightsRootModalScreen";
import { useLlmQuickWinsProvider } from "../../../../utilities/llm-quick-wins";
import { ValidActiveTerminalGuard } from "../../../../components/ValidActiveTerminalGuard/ValidActiveTerminalGuard";

export type RegisterRootScreenProps = StackScreenProps<
  ValidSetupRootParamList &
    RegisterRootParamList &
    AuthorizedAppRootScreenParamList,
  "RegisterRootScreen"
>;

const cartRevealAtWidth = 900;
const Stack = createStackHeaderNavigator<RegisterRootParamList>();

export const RegisterRootScreen = ({ navigation }: RegisterRootScreenProps) => {
  const { colors: Colors } = useThemeProvider();
  const { sale } = useSaleControllerProvider();
  const addItem = useAddItem({ navigation });
  const { terminal } = useActiveTerminal();

  const { strategyEnabled } = useLlmQuickWinsProvider();

  const terminalIsOpen =
    terminal && terminal.terminalOpenTime && !terminal.terminalCloseTime;

  const defaultScreenOptions = {
    contentStyle: { backgroundColor: Colors.surface },
    headerStyle: {
      backgroundColor: Colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: Colors.borderColor,
    },
  };

  useProductFilteredSearchListener(
    ({ productId }) => {
      addItem(productId);
    },
    [addItem],
  );
  const now = new Date();
  const localMidnight = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    0,
    0,
    0,
    0,
  );
  const utcTimeForLocalMidnight = localMidnight.toISOString();

  const shiftAiMarkup = terminalIsOpen ? (
    <View row centerV style={{ gap: 4 }}>
      {strategyEnabled("sales_period_summary_quick_win") ? (
        <Tooltip text="Summarize shift with AI">
          <TextButton
            icon={<Ionicons name="sparkles" size={12} />}
            textStyle={{ fontSize: 12, color: Colors.text, opacity: 0.6 }}
            onPress={() =>
              navigation.navigate("AiInsightsRootModalScreen", {
                screen: "AiInsightsScreen",
                startDate: terminal.terminalOpenTime?.toISOString() || "",
                endDate: undefined,
              })
            }
          >
            {`${formatDateToCalendar(
              terminal.terminalOpenTime as any,
              fullDateTimeFormat,
            )} - present`}
          </TextButton>
        </Tooltip>
      ) : (
        <View>
          <Text style={{ fontSize: 12, color: Colors.text, opacity: 0.6 }}>
            {`${formatDateToCalendar(
              terminal.terminalOpenTime as any,
              fullDateTimeFormat,
            )} - present`}
          </Text>
        </View>
      )}
    </View>
  ) : (
    <View>
      {strategyEnabled("sales_period_summary_quick_win") ? (
        <Tooltip text="Summarize day with AI">
          <TextButton
            icon={<Ionicons name="sparkles" size={12} />}
            textStyle={{ fontSize: 12, color: Colors.text, opacity: 0.6 }}
            onPress={() =>
              navigation.navigate("AiInsightsRootModalScreen", {
                screen: "AiInsightsScreen",
                startDate: utcTimeForLocalMidnight,
                endDate: undefined,
              })
            }
          >
            {`${formatDateToCalendar(
              new Date().toISOString(),
              "ddd MMM D YYYY",
            )} - present`}
          </TextButton>
        </Tooltip>
      ) : (
        <Text style={{ fontSize: 12, color: Colors.text, opacity: 0.6 }}>
          {`${formatDateToCalendar(
            new Date().toISOString(),
            "ddd MMM D YYYY",
          )} - present`}
        </Text>
      )}
    </View>
  );

  return (
    <LayoutProvider>
      <FilterMetadataController>
        <CreateTileProvider>
          <EditTileProvider>
            <RegisterProvider {...{ cartRevealAtWidth }}>
              <ValidateActiveSale runImmediately={true} />
              <ValidActiveTerminalGuard />
              <Stack.Navigator
                screenOptions={{
                  headerShown: false,
                }}
              >
                <Stack.Group
                  screenOptions={{
                    ...defaultScreenOptions,
                    headerTitle: ({ children, ...p }) => {
                      const orderNumber = sale?.invoiceNumber
                        ? `#${sale.invoiceNumber}`
                        : "";

                      const purchaseOrderNumber = sale?.purchaseOrderNumber
                        ? ` / PO#${sale.purchaseOrderNumber}`
                        : "";

                      return sale ? (
                        <VisuallyHidden>
                          <View style={{ alignItems: "center", gap: 2 }}>
                            <View
                              style={{
                                alignItems: "center",
                                paddingVertical: 4,
                                paddingHorizontal: 8,
                                // backgroundColor: "#00000005",
                                // borderColor: "#00000010",
                                // borderWidth: 1,
                                borderRadius: 12,
                              }}
                            >
                              <Text
                                style={{
                                  fontSize: 14,
                                  fontWeight: "bold",
                                  color: Colors.text,
                                }}
                              >{`${
                                terminal?.name ?? ""
                              } / ${sale.saleType.toUpperCase()} ${orderNumber} ${purchaseOrderNumber}`}</Text>
                            </View>
                            {shiftAiMarkup}
                          </View>
                        </VisuallyHidden>
                      ) : (
                        <VisuallyHidden>
                          <View style={{ alignItems: "center", gap: 2 }}>
                            <View style={{ gap: 4, alignItems: "center" }}>
                              <Text
                                style={{
                                  fontSize: 14,
                                  fontWeight: "bold",
                                  color: Colors.text,
                                }}
                              >{`${terminal?.name ?? ""}`}</Text>
                            </View>
                            {shiftAiMarkup}
                          </View>
                        </VisuallyHidden>
                      );
                    },
                    title: "",
                    headerShown: true,
                    headerLeft: () => (
                      <ViewStack centerVertical>
                        <RootDrawerToggle />
                        <Logo />
                      </ViewStack>
                    ),
                    headerRight: () => (
                      <ActiveUserAvatar
                        onPress={() =>
                          navigation.navigate("SwitchUserRootScreen")
                        }
                      />
                    ),
                  }}
                >
                  <Stack.Screen name="SellScreen" component={SellScreen} />
                </Stack.Group>
                <Stack.Group
                  screenOptions={{
                    ...defaultScreenOptions,
                    animationEnabled: true,
                    headerShown: false,
                    presentation: "modal",
                    cardStyle: { backgroundColor: Colors.surface },
                  }}
                >
                  <Stack.Screen
                    name="CheckoutRootScreen"
                    component={CheckoutRootScreen}
                  />
                </Stack.Group>
                <Stack.Group
                  screenOptions={{
                    presentation: "modal",
                    headerLeft: () => <ModalCloseButton />,
                    animationEnabled: true,
                    cardStyleInterpolator:
                      CardStyleInterpolators.forVerticalIOS,
                    headerShown: true,
                    cardStyle: {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <Stack.Screen
                    name="ProductScannerScreen"
                    component={ProductScannerScreen}
                    options={{
                      headerShown: false,
                    }}
                  />
                </Stack.Group>
                <Stack.Group
                  screenOptions={{
                    headerLeft: () => <ModalCloseButton />,
                    animationEnabled: true,
                    headerShown: true,
                    presentation: "modal",
                    title: "",
                    cardStyle: { backgroundColor: Colors.surface },
                  }}
                >
                  <Stack.Screen
                    name="AddNonPricedItemModalScreen"
                    component={AddNonPricedItemModalScreen}
                  />
                  <Stack.Screen
                    name="ConfirmAddScannedCustomerModalScreen"
                    component={ConfirmAddScannedCustomerModalScreen}
                  />
                  <Stack.Screen
                    name="ActionsRootScreen"
                    component={ActionsRootScreen}
                    options={{ headerShown: false }}
                  />
                  <Stack.Screen
                    name="CartItemModalRootScreen"
                    component={CartItemModalRootScreen}
                    options={{ headerShown: false }}
                  />
                  <Stack.Screen
                    name="AddGiftCardProductCartRootScreen"
                    component={AddGiftCardProductCartRootScreen}
                    options={{ headerShown: false }}
                  />
                  <Stack.Screen
                    name="AiInsightsRootModalScreen"
                    component={AiInsightsRootModalScreen}
                    options={{ headerShown: false }}
                  />
                  <Stack.Screen
                    options={{ title: "Create Quick Key", headerShown: false }}
                    name="CreateQuickKeyRootScreen"
                    //@ts-ignore
                    component={CreateQuickKeyRootScreen}
                  />
                  <Stack.Screen
                    options={{ title: "Edit Quick Key", headerShown: false }}
                    name="EditQuickKeyRootScreen"
                    component={EditQuickKeyRootScreen}
                  />
                  <Stack.Screen
                    name="SerialNumberRootScreen"
                    component={SerialNumberRootScreen}
                    options={{ headerShown: false }}
                  />
                  <Stack.Screen
                    name="MobileCartScreen"
                    component={MobileCartScreen}
                    options={{ title: "" }}
                  />
                  <Stack.Screen
                    name="VariantPickerModalScreen"
                    component={VariantPickerModalScreen}
                  />
                  <Stack.Screen
                    name="KitResolverModalScreen"
                    component={KitResolverModalScreen}
                  />
                  <Stack.Screen
                    name="ProductFilteredListScreen"
                    component={ProductFilteredListScreen}
                    options={{ title: "Select Product" }}
                  />
                </Stack.Group>
                <Stack.Group
                  screenOptions={{
                    headerStyle: {
                      backgroundColor: Colors.surface,
                    },
                    cardStyle: {
                      backgroundColor: "transparent",
                    },
                    headerShown: false,
                    animationEnabled: true,
                    presentation: "transparentModal",
                    cardStyleInterpolator:
                      CardStyleInterpolators.forModalPresentationIOS,
                  }}
                >
                  <Stack.Screen
                    name="PermissionsModalRootScreen"
                    component={PermissionsModalRootScreen}
                  />
                </Stack.Group>
              </Stack.Navigator>
            </RegisterProvider>
          </EditTileProvider>
        </CreateTileProvider>
      </FilterMetadataController>
    </LayoutProvider>
  );
};
